import type {UseQueryOptions} from '@tanstack/react-query';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';

import {fetcher} from '#lit/utils/http.js';
import {RequestMethod} from '#utils/http.js';

const POLICIES_ENDPOINT = '/api/v2/policies';
const DEFAULT_STALE_TIME = 5 * 60 * 1000;

type RecordId = number | string;

export const NAVIGATION_POLICY_REQUEST: PolicyCheckRequest = {
  application: ['app_admin'],
  audience: ['index', 'create'],
  signal_route: ['index'],
  destination_connection: ['index'],
  journey: ['index'],
  export_activity: ['index'],
  experiment: ['index'],
  source_connection: ['index'],
  flywheel_model: ['index'],
  governance_rule: ['index'],
  import_source: ['index'],
  identity: ['index'],
  review_audience: ['index'],
  organization: ['show'],
  external_media_library_connection: ['show'],
  user_invitation: ['new'],
  request_feature: ['create'],
  content: ['index'],
  channel: ['create'],
};

type PolicyEntity =
  | 'application'
  | 'audience'
  | 'audience_export'
  | 'dataset'
  | 'dataset_group'
  | 'destination_connection'
  | 'destination_platform'
  | 'export_activity'
  | 'experiment'
  | 'flywheel_model'
  | 'governance_rule'
  | 'import_source'
  | 'journey'
  | 'organization'
  | 'external_media_library_connection'
  | 'review_audience'
  | 'request_feature'
  | 'signal_route'
  | 'identity'
  | 'content'
  | 'channel'
  | 'source_connection'
  | 'team'
  | 'user'
  | 'user_invitation';

type PolicyAction =
  | 'index'
  | 'create'
  | 'show'
  | 'update'
  | 'destroy'
  | 'edit'
  | 'new'
  | (string & {});

interface PolicyActionWithContext {
  action: PolicyAction;
  context: RecordId[];
}

type PolicyActionItem = PolicyAction | PolicyActionWithContext;

type PolicyCheckRequest = Partial<Record<PolicyEntity, PolicyActionItem[]>>;

type PolicyActionResult = boolean | Record<string, boolean>;

type PolicyEntityResult = Partial<Record<PolicyAction, PolicyActionResult>>;

type PolicyCheckResponse = Partial<Record<PolicyEntity, PolicyEntityResult>>;

type PolicyCheckQueryOptions = Omit<
  UseQueryOptions<PolicyCheckResponse>,
  'queryKey' | 'queryFn'
>;

export const policyQueryKeys = {
  all: ['policies'] as const,
  checks: () => [...policyQueryKeys.all, 'check'] as const,
  check: (request: PolicyCheckRequest) =>
    [...policyQueryKeys.checks(), request] as const,
} as const;

export async function checkPolicies(
  request: PolicyCheckRequest,
  signal?: AbortSignal
): Promise<PolicyCheckResponse> {
  return fetcher<PolicyCheckResponse>(POLICIES_ENDPOINT, {
    method: RequestMethod.POST,
    body: request,
    signal,
  });
}

export function policyCheckQueryOptions(
  request: PolicyCheckRequest,
  options?: PolicyCheckQueryOptions
): UseQueryOptions<PolicyCheckResponse> {
  return {
    queryKey: policyQueryKeys.check(request),
    queryFn: ({signal}) => checkPolicies(request, signal),
    staleTime: DEFAULT_STALE_TIME,
    refetchOnWindowFocus: false,
    ...options,
  };
}

export function usePolicyCheck(
  request: PolicyCheckRequest,
  options?: PolicyCheckQueryOptions
) {
  return useQuery(policyCheckQueryOptions(request, options));
}

export function useInvalidatePolicies() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({queryKey: policyQueryKeys.all}),
    [queryClient]
  );
}

export function useInvalidatePolicyCheck() {
  const queryClient = useQueryClient();
  return useCallback(
    (request: PolicyCheckRequest) =>
      queryClient.invalidateQueries({queryKey: policyQueryKeys.check(request)}),
    [queryClient]
  );
}

export function isActionWithContext(
  item: PolicyActionItem
): item is PolicyActionWithContext {
  return typeof item === 'object' && 'action' in item && 'context' in item;
}

export function getSimplePermission(
  response: PolicyCheckResponse | undefined,
  entity: PolicyEntity,
  action: PolicyAction
): boolean {
  const result = response?.[entity]?.[action];
  return typeof result === 'boolean' ? result : false;
}

export function getContextPermissions(
  response: PolicyCheckResponse | undefined,
  entity: PolicyEntity,
  action: PolicyAction
): Record<string, boolean> {
  const result = response?.[entity]?.[action];
  return typeof result === 'object' ? result : {};
}

export function hasPermission(
  response: PolicyCheckResponse | undefined,
  entity: PolicyEntity,
  action: PolicyAction,
  recordId?: RecordId
): boolean {
  const result = response?.[entity]?.[action];
  if (typeof result === 'boolean') return result;
  if (typeof result === 'object' && recordId !== undefined) {
    return result[String(recordId)] ?? false;
  }

  return false;
}

export function buildPolicyRequest() {
  const request: PolicyCheckRequest = {};

  const builder = {
    check(entity: PolicyEntity, ...actions: PolicyAction[]) {
      request[entity] ??= [];
      request[entity]!.push(...actions);
      return builder;
    },
    checkWithContext(
      entity: PolicyEntity,
      action: PolicyAction,
      context: RecordId[]
    ) {
      request[entity] ??= [];
      request[entity]!.push({action, context});
      return builder;
    },
    build: (): PolicyCheckRequest => request,
  };

  return builder;
}

export type {
  PolicyEntity,
  PolicyAction,
  PolicyActionWithContext,
  PolicyActionItem,
  PolicyCheckRequest,
  PolicyActionResult,
  PolicyEntityResult,
  PolicyCheckResponse,
  PolicyCheckQueryOptions,
  RecordId,
};
